<script setup lang="ts">
/**
 * AccountView — `/settings/account` route.
 *
 * Read-only view of the current user's profile (from the live
 * `GET /api/cms/users/profile` endpoint, not the auth-store snapshot).
 * Plus a "Change password" button that opens ChangePasswordModal.
 *
 * The reason this reads from the profile query rather than the auth
 * store is that it should reflect any privileges granted out-of-band
 * (e.g. by another admin while you're logged in). The auth store's
 * snapshot is fixed at login.
 */
import { ref, computed } from 'vue'
import { KeyRound, Shield } from 'lucide-vue-next'
import PageHeader from '@/components/ui/PageHeader.vue'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Avatar from '@/components/ui/Avatar.vue'
import StatusDot from '@/components/ui/StatusDot.vue'
import Tag from '@/components/ui/Tag.vue'
import ChangePasswordModal from '../components/ChangePasswordModal.vue'
import { useProfileQuery } from '../queries'

const passwordModalOpen = ref(false)
const profile = useProfileQuery()

const privilegeCount = computed(() => profile.data.value?.privileges.length ?? 0)

/**
 * Privileges shown in alphabetical groups by domain prefix. Mirrors
 * the PermissionsDrawer's grouping but read-only.
 */
const groupedPrivileges = computed(() => {
  const all = profile.data.value?.privileges ?? []
  const sorted = [...all].sort((a, b) => a.localeCompare(b))
  return sorted
})
</script>

<template>
  <div>
    <PageHeader
      :title="$t('account.title')"
      :subtitle="$t('account.subtitle')"
    />

    <div v-if="profile.isLoading.value" class="flex justify-center py-12">
      <Spinner size="lg" />
    </div>

    <EmptyState
      v-else-if="profile.error.value"
      :title="$t('account.error.title')"
      :description="$t('account.error.description')"
    />

    <div v-else-if="profile.data.value" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <!-- Profile card (left) -->
      <Card class="lg:col-span-1">
        <template #head>{{ $t('account.profile.title') }}</template>

        <div class="flex items-center gap-3 mb-4">
          <Avatar :name="profile.data.value.userName" size="lg" />
          <div class="min-w-0">
            <p class="text-base font-semibold text-text-1 m-0 truncate">
              {{ profile.data.value.userName }}
            </p>
            <StatusDot
              :status="profile.data.value.external ? 'inactive' : 'active'"
            >
              {{ profile.data.value.external
                ? $t('iam.type.external')
                : $t('iam.type.internal') }}
            </StatusDot>
          </div>
        </div>

        <Button kind="ghost" :icon="KeyRound" @click="passwordModalOpen = true">
          {{ $t('account.changePassword.trigger') }}
        </Button>
      </Card>

      <!-- Privileges card (right) -->
      <Card class="lg:col-span-2">
        <template #head>
          <Shield :size="14" class="text-text-3" />
          {{ $t('account.privileges.title') }}
        </template>
        <template #tools>
          <span class="text-xs text-text-4 font-mono">
            {{ $t('account.privilegesCount', { n: privilegeCount }) }}
          </span>
        </template>

        <div v-if="groupedPrivileges.length === 0" class="text-sm text-text-4 py-2">
          {{ $t('account.privileges.none') }}
        </div>

        <div v-else class="flex flex-wrap gap-1.5">
          <Tag
            v-for="p in groupedPrivileges"
            :key="p"
            variant="brand"
          >{{ p }}</Tag>
        </div>

        <p class="mt-4 text-xs text-text-4 leading-relaxed">
          {{ $t('account.privileges.helper') }}
        </p>
      </Card>
    </div>

    <ChangePasswordModal v-model:open="passwordModalOpen" />
  </div>
</template>
